import { yupResolver } from '@hookform/resolvers/yup';
import { FormEventHandler, useCallback, useEffect, useState } from 'react';
import { FieldValues, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { usePostBaseManuscriptsImport } from '../../../../api/base-manuscripts/base-manuscripts';
import { BaseManuscriptSearchConditions } from '../../../../api/model';
import { useOptionsForBinary } from '../../../../hooks/api';
import yup from '../../../../utils/custom-yup';
import { executeDownload } from '../../../../utils/download';
import FileDrop from '../../../atoms/FileDrop';
import ErrorModal from '../../../modules/ErrorModal';
import Modal from '../../../modules/Modal';
import OkModal from '../../../modules/OkModal';
import SpinnerModal from '../../../modules/SpinnerModal';

interface Props {
  active: boolean;
  productionId: number;
  onClose: () => void;
  setConditionsAndSearch: (value: BaseManuscriptSearchConditions) => void;
}

/**
 * ベース原稿タブのインポートダイアログ
 * @param param0
 * @returns
 */
function BaseManualImportModal({ active, productionId, onClose, setConditionsAndSearch }: Props) {
  const {
    t,
    i18n: { language },
  } = useTranslation();

  // Form定義
  const schema = yup.object({
    comment: yup
      .string()
      .nullable()
      .transform((v) => (v ? (v as string) : undefined)),
    importFiles: yup.array().required(),
  });

  const {
    handleSubmit,
    setValue,
    reset,
    formState: { errors },
  } = useForm({ resolver: yupResolver(schema) });

  const [displayBaseManuscriptsImportOk, setDisplayBaseManuscriptsImportOk] = useState(false);
  const [importMsg, setImportMsg] = useState<string | undefined>('');

  // インポート処理定義
  const optionsForBinary = useOptionsForBinary(language);
  const {
    mutateAsync: BaseManuscriptsImportXmlAsync,
    isLoading: BaseManuscriptsImportLoading,
    error: BaseManuscriptsImportXmlError,
  } = usePostBaseManuscriptsImport(optionsForBinary);

  const [displayBaseManuscriptsImportXmlError, setDisplayBaseManuscriptsImportXmlError] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  useEffect(() => {
    setDisplayBaseManuscriptsImportXmlError(BaseManuscriptsImportXmlError != null);
    setIsSubmitting(false);
  }, [BaseManuscriptsImportXmlError]);

  // キャンセル
  const onCancelClicked = useCallback(() => {
    reset();
    onClose();
  }, [onClose, reset]);

  // インポート実行
  const onSubmit: FormEventHandler<HTMLFormElement> = useCallback(
    (e) => {
      e.preventDefault();

      void handleSubmit(async (data: FieldValues) => {
        if (data.importFiles.length > 0) {
          let filename: string | undefined = '';

          setIsSubmitting(true);
          await BaseManuscriptsImportXmlAsync(
            {
              productionId,
              data: { file: data.importFiles?.[0] },
            },
            {
              // API アクセスが成功したら、ダウンロードを実行
              onSuccess: (response) => {
                executeDownload(response);

                /* ファイル名取得 */
                const disposition = response.headers['content-disposition'];
                const type = response.headers['content-type'];
                if (disposition != null && type != null) {
                  const filenameRegex1 = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/;
                  const matches1 = filenameRegex1.exec(disposition);
                  if (matches1 != null && matches1[1] != null) {
                    filename = decodeURI(matches1[1].replace(/['"]/g, ''));
                  }
                  const filenameRegex2 = /filename\*=utf-8''((['"]).*?\2|[^;\n]*)/;
                  const matches2 = filenameRegex2.exec(disposition);
                  if (matches2 != null && matches2[1] != null) {
                    filename = decodeURI(matches2[1].replace(/['"]/g, ''));
                  }
                }
              },
            }
          );
          if (filename != null) {
            if (filename.indexOf('_Error') !== -1) {
              // ファイル名に「_Error」が存在する場合、ダイアログを表示
              setImportMsg('調整ファイルの取込みに失敗しました。ログを確認してください。');
              setDisplayBaseManuscriptsImportOk(true);
            } else {
              setImportMsg('調整ファイルを取込みました。');
              setDisplayBaseManuscriptsImportOk(true);
            }
          } else {
            setIsSubmitting(false);
          }
        }
      })();
    },
    [
      setIsSubmitting,
      handleSubmit,
      productionId,
      BaseManuscriptsImportXmlAsync,
      setDisplayBaseManuscriptsImportOk,
      setImportMsg,
    ]
  );

  return (
    <Modal active={active} additionalClassName="mtsp" displayCloseButton onClose={onCancelClicked}>
      <form onSubmit={onSubmit}>
        <fieldset disabled={isSubmitting} className="fieldset-reset">
          <div className="formarea1">
            <label htmlFor="">{t('添付ファイル')}</label>
            <div>
              <FileDrop id="importFiles" name="importFiles" setValue={setValue} />
              {errors.importFiles?.message != null && (
                <p className="form-error">{errors.importFiles.message.toString()}</p>
              )}
            </div>
          </div>
          <div className="btnfr btnfr2 mb0">
            <div>
              <button type="button" className="text-button" onClick={() => onCancelClicked()}>
                <span className="btn2 mr10 btnbg-g">
                  <span className="modal-close2">{t('キャンセル')}</span>
                </span>
              </button>
              <span>
                <input type="submit" id="" name="" value={t('インポート').toString()} className="btn1" />
              </span>{' '}
            </div>
          </div>
        </fieldset>
      </form>
      {BaseManuscriptsImportLoading && <SpinnerModal />}
      {/* 取込みエラーダイアログ */}
      {displayBaseManuscriptsImportXmlError && (
        <ErrorModal
          onClickOk={() => {
            setIsSubmitting(false);
            setDisplayBaseManuscriptsImportXmlError(false);
          }}
          error={BaseManuscriptsImportXmlError}
        />
      )}
      {/* 取込み完了ダイアログ */}
      {displayBaseManuscriptsImportOk && (
        <OkModal
          onClickOk={() => {
            setIsSubmitting(false);
            setDisplayBaseManuscriptsImportOk(false);
            reset();
            onClose();

            // 成功したら条件をクリアして一覧を再読込
            const next: BaseManuscriptSearchConditions = {
              production_id: productionId,
              l1: '',
              l2: '',
              l3: '',
              l4: '',
              subheading: '',
              judgment_ids: undefined,
              description_items: '',
            };
            // 検索実行など
            setConditionsAndSearch(next);
          }}
        >
          <div>{t(importMsg ?? '')}</div>
        </OkModal>
      )}
    </Modal>
  );
}
export default BaseManualImportModal;
